import { useState, useCallback, useRef } from 'react'
import { generateLocationReference } from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 15000

const getCreativeGuidelineSessionId = source =>
  source?.sessionId ||
  source?.session_id ||
  source?.creative_guideline_session_id ||
  source?.creativeGuidelineSessionId ||
  source?.creativeDirection?.sessionId ||
  source?.creativeDirection?.session_id ||
  source?.creativeDirection?.creative_guideline_session_id ||
  source?.creativeDirection?.creativeGuidelineSessionId ||
  null

const getVideoCreationMode = formData =>
  formData?.aiVideoFlowMode ||
  formData?.video_creation_mode ||
  formData?.videoCreationMode ||
  formData?.creativeDirection?.flowMode ||
  formData?.creativeDirection?.flow_mode ||
  null

const getCreativeGuidelinePayload = formData => {
  const sessionId = getCreativeGuidelineSessionId(formData)

  return getVideoCreationMode(formData) === 'advanced' && sessionId
    ? { creative_guideline_session: sessionId }
    : {}
}

const isRequestCancelled = error =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.message === 'canceled'

const getTaskIdFromResponse = response =>
  response?.data?.task_id || response?.data?.taskId || null

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Location reference generation failed'

/**
 * Custom hook to manage location reference generation and polling
 * Handles location map generation, editing, and batch updates
 */
export const useLocationReferences = ({
  formData,
  setFormData,
  videoId
} = {}) => {
  const {
    isPolling,
    progress: locationGenerationProgress,
    status: locationGenerationStatus,
    activeTaskId,
    startPolling,
    stopPolling
  } = useTaskPoller({ pollInterval: POLL_INTERVAL })

  const [isRequesting, setIsRequesting] = useState(false)
  const requestAbortRef = useRef(null)
  const activeTaskIdRef = useRef(null)
  const stopRequestedRef = useRef(false)
  const generationTokenRef = useRef(0)

  const updateLocationGenerationState = useCallback(
    updates => {
      setFormData(prev => {
        const referenceGeneration = prev.referenceGeneration || {}
        const previousState = referenceGeneration.locations || {}
        const hasTaskIdUpdate = Object.prototype.hasOwnProperty.call(
          updates,
          'taskId'
        )
        const taskId = hasTaskIdUpdate
          ? updates.taskId
          : previousState.taskId || activeTaskIdRef.current || null

        return {
          ...prev,
          referenceGeneration: {
            ...referenceGeneration,
            locations: {
              ...previousState,
              ...updates,
              taskId,
              updatedAt: new Date().toISOString()
            }
          }
        }
      })
    },
    [setFormData]
  )

  const runLocationReferenceTask = useCallback(
    async (requestData, onSuccess, errorLabel) => {
      const runToken = generationTokenRef.current + 1
      generationTokenRef.current = runToken
      stopRequestedRef.current = false
      activeTaskIdRef.current = null

      if (requestAbortRef.current) {
        requestAbortRef.current.abort()
      }

      const abortController = new AbortController()
      requestAbortRef.current = abortController
      setIsRequesting(true)
      updateLocationGenerationState({
        status: 'requesting',
        taskId: null,
        progress: 0,
        error: null
      })

      const isCurrentRun = () =>
        generationTokenRef.current === runToken && !stopRequestedRef.current

      try {
        const locationRes = await generateLocationReference(requestData, {
          signal: abortController.signal
        })

        if (!isCurrentRun()) return null

        const taskId = getTaskIdFromResponse(locationRes)
        if (!taskId) {
          throw new Error('No task ID returned from location generation')
        }

        activeTaskIdRef.current = taskId
        setIsRequesting(false)
        updateLocationGenerationState({
          status: 'processing',
          taskId,
          progress: 0,
          error: null
        })

        const result = await startPolling(taskId)
        if (!isCurrentRun()) return null

        if (result?.status === 'cancelled') {
          updateLocationGenerationState({
            status: 'stopped',
            taskId,
            progress: locationGenerationProgress || 0
          })
          return null
        }

        if (result && result.status === 'success' && result.output) {
          onSuccess(result.output)
          updateLocationGenerationState({
            status: 'success',
            taskId,
            progress: 100,
            error: null
          })
        }

        return result
      } catch (error) {
        if (
          isRequestCancelled(error) ||
          stopRequestedRef.current ||
          generationTokenRef.current !== runToken
        ) {
          updateLocationGenerationState({
            status: 'stopped',
            taskId: activeTaskIdRef.current,
            progress: locationGenerationProgress || 0
          })
          return null
        }

        console.error(errorLabel, error)
        updateLocationGenerationState({
          status: 'failed',
          taskId: activeTaskIdRef.current,
          error: getErrorMessage(error)
        })
        throw error
      } finally {
        if (generationTokenRef.current === runToken) {
          requestAbortRef.current = null
          setIsRequesting(false)
        }
      }
    },
    [
      locationGenerationProgress,
      setFormData,
      startPolling,
      updateLocationGenerationState
    ]
  )

  const stopLocationReferenceGeneration = useCallback(() => {
    stopRequestedRef.current = true
    generationTokenRef.current += 1

    if (requestAbortRef.current) {
      requestAbortRef.current.abort()
      requestAbortRef.current = null
    }

    stopPolling()
    setIsRequesting(false)
    updateLocationGenerationState({
      status: 'stopped',
      taskId: activeTaskIdRef.current || activeTaskId || null,
      progress: locationGenerationProgress || 0
    })
  }, [
    activeTaskId,
    locationGenerationProgress,
    stopPolling,
    updateLocationGenerationState
  ])

  /**
   * Generate location map references
   */
  const generateLocationMap = useCallback(
    async (locationMapObj, existingUris) => {
      return runLocationReferenceTask(
        {
          location_map_with_prompts: locationMapObj,
          existing_location_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.locationReference,
          video_id: videoId,
          ...getCreativeGuidelinePayload(formData)
        },
        output => {
          if (output) {
            setFormData(prev => ({
              ...prev,
              locationReference: output
            }))
          }
        },
        'Error generating location references:'
      )
    },
    [
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.locationReference,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runLocationReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Add new location reference
   */
  const handleAddLocationReference = useCallback(
    async newLocationMap => {
      try {
        return runLocationReferenceTask(
          {
            location_map_with_prompts: {
              ...(formData?.locationMap || {}),
              ...newLocationMap
            },
            existing_location_uris: formData?.locationReference,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...newLocationMap
              }
            }))
          },
          'Error adding location reference:'
        )
      } catch (error) {
        console.error('Error adding location reference:', error)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runLocationReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Edit existing location reference
   */
  const handleEditLocationReference = useCallback(
    async editingLocationPrompt => {
      try {
        const editingKeys = Object.keys(editingLocationPrompt)

        // Remove old URIs for edited locations
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        return runLocationReferenceTask(
          {
            location_map_with_prompts: {
              ...(formData?.locationMap || {}),
              ...editingLocationPrompt
            },
            existing_location_uris: filteredLocationUris,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...editingLocationPrompt
              }
            }))
          },
          'Error editing location reference:'
        )
      } catch (error) {
        console.error('Error editing location reference:', error)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runLocationReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Batch update multiple locations
   */
  const handleBatchUpdateLocations = useCallback(
    async (updatedLocationMap, modifiedKeys) => {
      try {
        // Filter out URIs for modified keys
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        return runLocationReferenceTask(
          {
            location_map_with_prompts: updatedLocationMap,
            existing_location_uris: filteredLocationUris,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...updatedLocationMap
              }
            }))
          },
          'Error batch updating location references:'
        )
      } catch (error) {
        console.error('Error batch updating location references:', error)
      }
    },
    [
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.locationReference,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runLocationReferenceTask,
      setFormData,
      videoId
    ]
  )

  return {
    // State
    isGeneratingLocationReference: isPolling || isRequesting,
    locationGenerationProgress,
    locationGenerationStatus,

    // Actions
    generateLocationMap,
    handleAddLocationReference,
    handleEditLocationReference,
    handleBatchUpdateLocations,
    stopLocationReferenceGeneration
  }
}

export default useLocationReferences
